/** @file The optimistic first apply of a batch: per-command minted ids, pinned create ids, CR-011 errors. */
import { applyCommand, type ApplyEnv, type ApplyOutcome } from '@pose2d/commands/applyCommand';
import { lookupCommand } from '@pose2d/commands/lookupCommand';
import { throwError } from '@pose2d/format/throwError';
import { toPose2DError } from '@pose2d/format/toPose2DError';
import type { CommandCall, Doc, Id, IdPrefix, Warning } from '@pose2d/format/types';
import type { PendingCommand } from '../../stores/types';
import { pinCreatedId } from './pinCreatedId';

/** The view after the batch, the commands as they will be sent, and what they created and warned. */
export interface LocalApply {
  doc: Doc;
  pending: PendingCommand[];
  created: Record<string, Id>;
  warnings: Warning[];
}

/** applyCommand; a refusal names its command as the server's applyBatch does (CR-011). */
function applyAt(doc: Doc, call: CommandCall, env: ApplyEnv, index: number): ApplyOutcome {
  try {
    return applyCommand(doc, call, env);
  } catch (thrown) {
    const error = toPose2DError(thrown);
    if (error === null) throw thrown;
    const { path, hint } = error;
    const extra = { ...(path === undefined ? {} : { path }), ...(hint === undefined ? {} : { hint }) };
    return throwError(error.code, { ...error.params, commandIndex: index, command: call.name }, extra);
  }
}

/** Applies `calls` to `doc` in order (throws the first refusal, keeping nothing); `now` is the doc's updatedAt so a
 *  later replay of the same commands gives the same view (E8-R5). */
export function localApply(doc: Doc, calls: readonly CommandCall[], actor: string, mint: (prefix: IdPrefix) => Id): LocalApply {
  const out: LocalApply = { doc, pending: [], created: {}, warnings: [] };
  for (const [index, call] of calls.entries()) {
    const minted: Id[] = [];
    const newId = (prefix: IdPrefix): Id => {
      const id = mint(prefix);
      minted.push(id);
      return id;
    };
    const applied = applyAt(out.doc, call, { lookup: lookupCommand, now: doc.meta.updatedAt, actor, newId }, index);
    const sent = pinCreatedId(call, applied.result.created);
    out.doc = applied.doc;
    out.pending.push({ name: sent.name, input: sent.input, ids: minted });
    Object.assign(out.created, applied.result.created);
    out.warnings.push(...applied.result.warnings);
  }
  return out;
}
